import { Link } from 'react-router-dom';
import { services } from '../../../mocks/studios';

export default function BrandStory() {
  const timeline = [
    { year: '2019', title: 'The Problem We Noticed', desc: 'Our founders — photographers and filmmakers themselves — kept hitting the same wall: finding affordable, professional studio space in major cities was frustratingly complex, overpriced, and inefficient.' },
    { year: '2020', title: 'The First Studio Network', desc: 'We launched with 3 studio locations in New York City, focused on making the booking process fast, transparent, and fully online. Creators immediately responded.' },
    { year: '2022', title: 'Expansion to 4 Cities', desc: 'Rapid growth took us to Los Angeles, Chicago, and Miami. We expanded our studio types to include video production, podcast booths, and content creation spaces.' },
    { year: '2024', title: '50+ Studios. 6 Cities. 10,000+ Creators.', desc: 'Today, Book My Studio Space is the trusted platform for thousands of creators, brands, and agencies who need professional studio access without the commitment.' },
  ];

  return (
    <section id="brand-story" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — Story */}
          <div>
            <span className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-3 block">Our Story</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-6 leading-tight">
              Built for Creators Who Demand Professional Results
            </h2>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed mb-8">
              Book My Studio Space was founded with a simple belief: every creative professional deserves access to a professional environment — regardless of whether they own a studio, have a lease, or work with a major agency.
            </p>
            <p className="text-stone-500 text-base leading-relaxed mb-10">
              We\'ve removed every barrier between your creative vision and your best work. Browse, book, and create — in a space designed to make you look, sound, and perform at your absolute best.
            </p>

            {/* Timeline */}
            <div className="space-y-6">
              {timeline.map((item, idx) => (
                <div key={item.year} className="flex items-start gap-5">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-amber-100 border-2 border-amber-400 flex items-center justify-center">
                      <span className="text-amber-700 font-bold text-xs">{item.year}</span>
                    </div>
                    {idx < timeline.length - 1 && (
                      <div className="w-0.5 bg-amber-100 mt-2" style={{ height: '3rem' }} />
                    )}
                  </div>
                  <div className="pt-2">
                    <p className="text-stone-900 font-semibold text-base mb-1">{item.title}</p>
                    <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Studio overview */}
          <div className="space-y-6">
            {/* Feature image */}
            <div className="rounded-2xl overflow-hidden h-64">
              <img
                src="https://public.readdy.ai/ai/img_res/032808875e80b1ee5f4e06fe9e728a5f.jpg"
                alt="Book My Studio Space team"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Mission */}
            <div className="bg-stone-900 rounded-2xl p-7 text-white">
              <p className="text-amber-400 font-bold text-xs uppercase tracking-widest mb-3">Our Mission</p>
              <p className="text-white font-serif text-xl font-bold mb-4 leading-snug">
                &ldquo;To make professional studio access as easy as booking a rideshare — fast, reliable, and available to every creator.&rdquo;
              </p>
              <p className="text-stone-400 text-sm leading-relaxed">
                Every decision we make — from the studios we choose, to the equipment we stock, to the booking experience we build — is guided by one question: does this help creators do their best work?
              </p>
            </div>

            {/* Studio type cards */}
            <div className="grid grid-cols-2 gap-3">
              {services.slice(0, 4).map((s) => (
                <Link
                  key={s.id}
                  to={s.slug}
                  className="group flex items-center gap-3 bg-stone-50 hover:bg-amber-50 border border-stone-200 hover:border-amber-200 rounded-xl p-4 transition-all cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className={`${s.icon} text-amber-600 text-base`} />
                  </div>
                  <span className="text-stone-700 text-xs font-semibold leading-tight">{s.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
